"use client";
import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import "../../scss/_learn-investing.scss";
import heroImage from '../../../public/icons/hero.png';
import roundarrow from '../../../public/icons/roundarrow.png';
import energySectorImage from '../../../public/icons/energySector.png';
import databacked from '../../../public/icons/databacked.png';
import expertwritten from '../../../public/icons/expertwritten.png';
import insites from '../../../public/icons/insites.png';
import updatemonthly from '../../../public/icons/updatemonthly.png';
import { getArticleDetailBySlug, getArticleListByCategory } from '@/services/publicService';
import LoginPopUpPage from '@/sharedComponent/LoginPopUp';

const LEARNING_TABS = [
  { label: 'Oil and Gas Investment Basics', slug: 'investment-strategies' },
  { label: 'Royalties, Rights & Ownership', slug: 'energy-investment' },
  { label: 'Industry Segments and Technical Terms', slug: 'niche-investment' },
];

const BEGINNER_JOURNEY_ARTICLES_FIXED = [
  {
    title: "Oil Well Investment Opportunities: How to Start Investing in Oil and Gas Wells",
    slug: "oil-well-investment-opportunities-how-to-start-investing-in-oil-and-gas-wells-69396e0dc2c33",
    image: energySectorImage,
  },
  {
    title: "What Is Upstream in Oil and Gas Industry? Definition, Process & Examples",
    slug: "what-is-upstream-in-the-oil-and-gas-industry-definitions-key-processes-and-sector-comparisons-697b662230634",
    image: energySectorImage,
  },
  {
    title: "A Beginner’s Guide to Oil and Gas Exploration Investment: How It Works, Risks, and Opportunities",
    slug: "a-beginners-guide-to-oil-and-gas-exploration-investment-how-it-works-risks-and-opportunities-69944a52bb978",
    image: energySectorImage,
  },
];

const INDUSTRY_SEGMENTS_ARTICLES = [
  {
    title: "How to Invest in Crude Oil: Definition, Benefits, Risks & Strategies",
    slug: "how-to-invest-in-crude-oil-definition-benefits-risks-strategies-69ef3c0a8c6ea",
    image: energySectorImage,
  },
  {
    title: "Understanding Oil Futures: A Step-by-Step Guide to Trading",
    slug: "understanding-oil-futures-a-step-by-step-guide-to-trading-69f0fa3d6e74b",
    image: energySectorImage,
  },
  {
    title: "How Natural Gas Drilling Works: Techniques, Technology, and Environmental Considerations",
    slug: "how-natural-gas-drilling-works-techniques-technology-and-environmental-considerations-69b56cc4a47dc",
    image: energySectorImage,
  },
];

const ADVANCED_ARTICLES = [
  {
    title: "A Guide to Year-End Tax Benefits for Oil and Gas Investors",
    slug: "a-guide-to-year-end-tax-benefits-for-oil-and-gas-investors-693a916a3f62a",
    image: energySectorImage,
  },
  {
    title: "A Beginner’s Guide to Intangible Drilling Costs (IDCs) and Tax Deductions",
    slug: "a-beginners-guide-to-intangible-drilling-costs-idcs-and-tax-deductions-693985810d1cf",
    image: energySectorImage,
  },
  {
    title: "Tax Deduction for Oil and Gas: You Need to Know",
    slug: "tax-deduction-for-oil-and-gas-you-need-to-know-693a8fbae682f",
    image: energySectorImage,
  },
];

const INVESTMENT_STRATEGIES_ARTICLES = [
  {
    title: "How to Invest in Oil: A Beginner’s Guide to Direct, Indirect, and Diverse Options",
    slug: "how-to-invest-in-oil-a-beginners-guide-to-direct-indirect-and-diverse-options-69e759698b852",
    image: energySectorImage,
  },
  {
    title: "Understanding the Natural Gas Drilling Process: A Step-by-Step Guide",
    slug: "understanding-the-natural-gas-drilling-process-a-step-by-step-guide-69b9027bab157",
    image: energySectorImage,
  },
  {
    title: "Beginner’s Guide to Upstream Crude Oil Fundamentals | Exploration & Production Basics",
    slug: "beginners-guide-to-upstream-crude-oil-fundamentals-698f38d0b601f",
    image: energySectorImage,
  },
];

const ROYALTIES_RIGHTS_OWNERSHIP_ARTICLES = [
  {
    title: "How To Invest in Royalty Income",
    slug: "how-to-invest-in-royalty-income-693aa85e22ee2",
    image: energySectorImage,
  },
  {
    title: "Oil and Gas Royalties: How They Work, Payments & Investing",
    slug: "oil-and-gas-royalties-how-they-work-payments-investing-69b2e55b9b021",
    image: energySectorImage,
  },
  {
    title: "Oil and Gas Limited Partnership Investments: An Overview",
    slug: "oil-and-gas-limited-partnership-investments-an-overview-697338162afb5",
    image: energySectorImage,
  },
];

const BEGINNER_JOURNEY_ARTICLES = [
  {
    title: "Oil Well Investment Opportunities: How to Start Investing in Oil and Gas Wells",
    slug: "oil-well-investment-opportunities-how-to-start-investing-in-oil-and-gas-wells-69396e0dc2c33",
    image: energySectorImage,
  },
  {
    title: "What Is Upstream in Oil and Gas Industry? Definition, Process & Examples",
    slug: "what-is-upstream-in-the-oil-and-gas-industry-definitions-key-processes-and-sector-comparisons-697b662230634",
    image: energySectorImage,
  },
  {
    title: "A Beginner’s Guide to Oil and Gas Exploration Investment: How It Works, Risks, and Opportunities",
    slug: "a-beginners-guide-to-oil-and-gas-exploration-investment-how-it-works-risks-and-opportunities-69944a52bb978",
    image: energySectorImage,
  },
];

const truncateWords = (text, limit = 16) => {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  return words.length > limit ? `${words.slice(0, limit).join(' ')}...` : words.join(' ');
};

export default function LearnInvesting() {
  const [activeTab, setActiveTab] = useState(LEARNING_TABS[0].label);
  const [selectedLevel, setSelectedLevel] = useState("Beginner");
  const [tabArticles, setTabArticles] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);
  const [beginnerArticles, setBeginnerArticles] = useState([]);
  const [beginnerLoading, setBeginnerLoading] = useState(false);
  const [industryArticles, setIndustryArticles] = useState([]);
  const [industryLoading, setIndustryLoading] = useState(false);
  const [advancedArticles, setAdvancedArticles] = useState([]);
  const [advancedLoading, setAdvancedLoading] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  const activeTabSlug = LEARNING_TABS.find((t) => t.label === activeTab)?.slug;
  const activeTabConfig = {
    'investment-strategies': {
      articles: INVESTMENT_STRATEGIES_ARTICLES,
      viewMoreHref: '/category/foundational-oil-gas',
    },
    'energy-investment': {
      articles: ROYALTIES_RIGHTS_OWNERSHIP_ARTICLES,
      viewMoreHref: '/category/royalties',
    },
  }[activeTabSlug];

  useEffect(() => {
    if (!activeTabSlug) return;
    if (activeTabConfig) {
      let cancelled = false;
      (async () => {
        setTabLoading(true);
        try {
          const articles = await Promise.all(
            activeTabConfig.articles.map(async (article) => {
                const resp = await getArticleDetailBySlug(article.slug);
                const data = resp?.data || {};
              const takeaways = data.takeaways;
              const takeawaysText = Array.isArray(takeaways) ? takeaways.join(' ') : takeaways || '';
              return {
                ...article,
                title: data.title || article.title,
                description: truncateWords(takeawaysText || data.excerpt || data.description || ''),
                image: data.image?.url || data.image || data.featured_image || '',
              };
            })
          );
          if (!cancelled) setTabArticles(articles);
        } finally {
          if (!cancelled) setTabLoading(false);
        }
      })();
      return () => { cancelled = true; };
    }
    let cancelled = false;
    (async () => {
      setTabLoading(true);
      try {
        const resp = await getArticleListByCategory(activeTabSlug);
        if (cancelled) return;
        const list = Array.isArray(resp) ? resp : (resp?.data || []);
        setTabArticles(list);
      } finally {
        if (!cancelled) setTabLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTabSlug]);

  useEffect(() => {
    if (selectedLevel !== 'Beginner') return;
    let cancelled = false;
    (async () => {
      setBeginnerLoading(true);
      try {
        const articles = await Promise.all(
          BEGINNER_JOURNEY_ARTICLES_FIXED.map(async (article) => {
            const resp = await getArticleDetailBySlug(article.slug);
            const data = resp?.data || {};
            const takeaways = data.takeaways;
            const takeawaysText = Array.isArray(takeaways) ? takeaways.join(' ') : takeaways || '';
            return {
              ...article,
              title: data.title || article.title,
              description: truncateWords(takeawaysText || data.excerpt || data.description || '', 30),
              image: data.image?.url || data.image || data.featured_image || '',
            };
          })
        );
        if (!cancelled) setBeginnerArticles(articles);
      } finally {
        if (!cancelled) setBeginnerLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedLevel]);

  useEffect(() => {
    if (selectedLevel !== 'Intermediate') return;
    let cancelled = false;
    (async () => {
      setIndustryLoading(true);
      try {
        const articles = await Promise.all(
          INDUSTRY_SEGMENTS_ARTICLES.map(async (article) => {
            const resp = await getArticleDetailBySlug(article.slug);
            const data = resp?.data || {};
            const takeaways = data.takeaways;
            const takeawaysText = Array.isArray(takeaways) ? takeaways.join(' ') : takeaways || '';
            return {
              ...article,
              title: data.title || article.title,
              description: truncateWords(takeawaysText || data.excerpt || data.description || '', 30),
              image: data.image?.url || data.image || data.featured_image || '',
            };
          })
        );
        if (!cancelled) setIndustryArticles(articles);
      } finally {
        if (!cancelled) setIndustryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedLevel]);

  useEffect(() => {
    if (selectedLevel !== 'Advanced') return;
    let cancelled = false;
    (async () => {
      setAdvancedLoading(true);
      try {
        const articles = await Promise.all(
          ADVANCED_ARTICLES.map(async (article) => {
            const resp = await getArticleDetailBySlug(article.slug);
            const data = resp?.data || {};
            const takeaways = data.takeaways;
            const takeawaysText = Array.isArray(takeaways) ? takeaways.join(' ') : takeaways || '';
            return {
              ...article,
              title: data.title || article.title,
              description: truncateWords(takeawaysText || data.excerpt || data.description || '', 30),
              image: data.image?.url || data.image || data.featured_image || '',
            };
          })
        );
        if (!cancelled) setAdvancedArticles(articles);
      } finally {
        if (!cancelled) setAdvancedLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedLevel]);

  const learningCardsFallback = [
    {
      title: "What Drives Oil Prices?",
      description: "Learn the key factors such as global supply, geopolitical events, and market sentiment, that cause oil prices to rise and fall, and how they impact your investment strategy.",
      image: energySectorImage
    },
    {
      title: "Upstream vs. Downstream: What Investors Need to Know",
      description: "Understand the energy value chain from exploration to refining. This guide helps you identify where each segment fits and how to spot investment potential in each.",
      image: energySectorImage
    },
    {
      title: "A Beginner's Guide to Energy Investment Strategies",
      description: "Explore basic strategies for investing in oil, gas, and renewables. Learn how to evaluate risk, time your investments, and build a portfolio with long-term potential.",
      image: energySectorImage
    }
  ];

  const beginnerGuides = [
    {
      title: "Getting Started with Oil & Gas Investment",
      description: "This guide covers everything from how the industry works to what to look for in your first investment. Perfect for anyone starting with little to no background knowledge in energy investing."
    },
    {
      title: "How the Energy Value Chain Works",
      description: "Follow the journey of energy from exploration and drilling to consumption. Learn how upstream, midstream, and downstream sectors work together and where investors typically engage with each."
    },
    {
      title: "Beginner's Toolkit: Terms & Metrics You Should Know",
      description: "Get familiar with key terms, metrics, and performance indicators used in the energy sector. This toolkit helps you understand reports, follow market updates, and communicate more confidently."
    }
  ];

  const whyLearnFeatures = [
    {
      icon: insites,
      title: "Actionable Insights",
      description: "Learn with content beyond theory. Every guide is focused on helping you apply what you've learned to real investment decisions."
    },
    {
      icon: databacked,
      title: "Data-Backed Content",
      description: "Our articles are grounded in credible data, market research, and current trends to bring you insights you can count on."
    },
    {
      icon: expertwritten,
      title: "Expert-Written Guides",
      description: "All learning materials are created and reviewed by professionals with extensive experience in oil, gas, and energy finance."
    },
    {
      icon: updatemonthly,
      title: "Updated Monthly",
      description: "We regularly update our library to reflect the latest shifts in the market, so you stay informed and ahead of the curve."
    }
  ];

  return (
    <>
    <div className="learn-investing-page-li">
      {/* Hero Section */}
      <section className="hero-section-li">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="hero-content-li">
                <span className="hero-label-li">LEARN INVESTING</span>
                <h1 className="hero-title-li">Start Learning How to Invest in Energy Industry</h1>
                <p className="hero-description-li">
                  Investing in energy doesn&apos;t have to be overwhelming. We&apos;ve built a focused learning hub to help you cut through the noise, understand how the oil and gas market works, and make confident investment decisions suitable for all stages, from beginner to expert.
                </p>
                <button className="btn-hero-li" onClick={() => setShowLoginPopup(true)}>Start Free</button>
              </div>
              <div className="hero-image-wrapper-li">
                <Image 
                  src="/icons/learn-investing.png"
                  alt="Oil Refinery" 
                  className="hero-image-li"
                  fill
                  priority
                  style={{ objectFit: 'cover' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Explore Key Learning Areas Section */}
      <section className="learning-areas-section-li">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className='heading-wrap-li'>
              <h2 className="section-title-li">Explore Key Learning Areas in Energy Investment</h2>
              <p className="section-description-li">
                Master the basic terms, market drivers, and what sets oil and gas energy apart from other sectors. These guides break down core concepts into simple, practical insights you can apply.
              </p>
              </div>
            </div>
          </div>
          
          <div className="tabs-container-li">
            {LEARNING_TABS.map((tab) => (
              <button
                key={tab.slug}
                className={`tab-button-li ${activeTab === tab.label ? "active-li" : ""}`}
                onClick={() => setActiveTab(tab.label)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tab-content-li">
            <p className="tab-description-li">
              From global supply and demand of oil to global politics, learn which economic trends shape oil pricing and what are the most important factors to consider when looking to invest.
            </p>

            <div className="row learning-cards-row-li">
              {tabLoading ? (
                <div className="col-12 text-center py-4"><p>Loading...</p></div>
              ) : tabArticles.length > 0 ? (
                tabArticles.map((article, index) => {
                  const imgSrc = typeof article?.image === 'string' ? article.image : article?.image?.url;
                  const href = article?.slug ? `/article/${article.slug}` : '#';
                  return (
                    <div key={article?.id || index} className="col-lg-12 mb-4">
                      <div className="learning-card-li">
                        <div className="learning-card-content-li">
                          <h3 className="learning-card-title-li">{article?.title || ''}</h3>
                          <p className="learning-card-description-li">{article?.excerpt || article?.description || ''}</p>
                          <Link href={href} className="learning-card-link-li">
                            Read More
                            <Image src={roundarrow} alt='roundarrow' />
                          </Link>
                        </div>
                        <div className="learning-card-image-li">
                          <Image
                            src={imgSrc && typeof imgSrc === 'string' && imgSrc.trim() ? imgSrc : energySectorImage}
                            alt={article?.title || 'article'}
                            fill
                            style={{ objectFit: 'cover' }}
                            unoptimized={typeof imgSrc === 'string' && imgSrc.trim() !== ''}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                learningCardsFallback.map((card, index) => (
                  <div key={index} className="col-lg-12 mb-4">
                    <div className="learning-card-li">
                      <div className="learning-card-content-li">
                        <h3 className="learning-card-title-li">{card.title}</h3>
                        <p className="learning-card-description-li">{card.description}</p>
                        <a href="#" className="learning-card-link-li">
                          Read More
                          <Image src={roundarrow} alt='roundarrow' />
                        </a>
                      </div>
                      <div className="learning-card-image-li">
                        <Image src={card.image} alt={card.title} fill style={{ objectFit: 'cover' }} />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="text-center mt-4">
              <Link href={activeTabConfig?.viewMoreHref || `/category/${activeTabSlug}`} className="btn-view-more-li" style={{ textDecoration: 'none' }}>View More Posts</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Where Are You on Your Journey Section */}
      <section className="journey-section-li">
        <div className="container">
          <div className="row">
            <div className="col-lg-9">
              <div className='head-hold-title'>
              <h2 className="section-title-li">Where Are You on Your Journey?</h2>
              <p className="section-description-li">
                Whether you&apos;re just starting out or are a seasoned professional, we&apos;ve created learning tracks to match your level. Choose the path that fits you best and start building your energy investment knowledge, step-by-step, at your own pace.
              </p>
              </div>
            </div>
            <div className="col-lg-3">
              <div className="level-selector-li">
                <select 
                  className="level-dropdown-li"
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(e.target.value)}
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
            </div>
          </div>

          {selectedLevel === "Beginner" && (
            <div className="level-content-li">
              <h3 className="level-subtitle-li">Beginner&apos;s Guides</h3>
              <p className="level-description-li">
                Step-by-step resources built to help new investors understand the fundamentals. These guides cover the basics so you can start strong and make informed decisions from day one.
              </p>
              
              <div className="row beginner-guides-row-li">
                {beginnerLoading ? (
                  <div className="col-12 text-center py-4"><p>Loading...</p></div>
                ) : beginnerArticles.length > 0 ? (
                  beginnerArticles.map((article, index) => {
                    const href = article?.slug ? `/article/${article.slug}` : '#';
                    return (
                      <div key={article?.id || index} className="col-lg-4 col-md-6 mb-4">
                        <div className="guide-card-li">
                          <h4 className="guide-card-title-li">{article?.title || ''}</h4>
                          <p className="guide-card-description-li">{article?.description || ''}</p>
                          <Link href={href} className="guide-card-link-li">
                            Learn More
                            <Image src={roundarrow} alt='roundarrow' />
                          </Link>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  beginnerGuides.map((guide, index) => (
                    <div key={index} className="col-lg-4 col-md-6 mb-4">
                      <div className="guide-card-li">
                        <h4 className="guide-card-title-li">{guide.title}</h4>
                        <p className="guide-card-description-li">{guide.description}</p>
                        <a href="#" className="guide-card-link-li">
                          Learn More
                          {/* <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 12L12 4M12 4H6M12 4V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg> */}
                          <Image src={roundarrow} alt='roundarrow' />
                        </a>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {selectedLevel === "Intermediate" && (
            <div className="level-content-li">
              <h3 className="level-subtitle-li">Industry Segments and Technical Terms</h3>
              <p className="level-description-li">
                Explore the practical side of oil and gas investing through market mechanics, technical processes, and tradeable segments.
              </p>

              <div className="row beginner-guides-row-li">
                {industryLoading ? (
                  <div className="col-12 text-center py-4"><p>Loading...</p></div>
                ) : industryArticles.length > 0 ? (
                  industryArticles.map((article, index) => {
                    const href = article?.slug ? `/article/${article.slug}` : '#';
                    return (
                      <div key={article?.id || index} className="col-lg-4 col-md-6 mb-4">
                        <div className="guide-card-li">
                          <h4 className="guide-card-title-li">{article?.title || ''}</h4>
                          <p className="guide-card-description-li">{article?.description || ''}</p>
                          <Link href={href} className="guide-card-link-li">
                            Learn More
                            <Image src={roundarrow} alt='roundarrow' />
                          </Link>
                        </div>
                      </div>
                    );
                  })
                ) : null}
              </div>
            </div>
          )}

          {selectedLevel === "Advanced" && (
            <div className="level-content-li">
              <h3 className="level-subtitle-li">Advanced Guides</h3>
              <p className="level-description-li">
                Dive into tax planning and advanced investor strategies designed for experienced oil and gas investors.
              </p>

              <div className="row beginner-guides-row-li">
                {advancedLoading ? (
                  <div className="col-12 text-center py-4"><p>Loading...</p></div>
                ) : advancedArticles.length > 0 ? (
                  advancedArticles.map((article, index) => {
                    const href = article?.slug ? `/article/${article.slug}` : '#';
                    return (
                      <div key={article?.id || index} className="col-lg-4 col-md-6 mb-4">
                        <div className="guide-card-li">
                          <h4 className="guide-card-title-li">{article?.title || ''}</h4>
                          <p className="guide-card-description-li">{article?.description || ''}</p>
                          <Link href={href} className="guide-card-link-li">
                            Learn More
                            <Image src={roundarrow} alt='roundarrow' />
                          </Link>
                        </div>
                      </div>
                    );
                  })
                ) : null}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Why Learn with Invest in Energy Section */}
      <section className="why-learn-section-li">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <h2 className="section-title-li">Why Learn with Invest in Energy?</h2>
            </div>
            <div className="col-lg-5">
              <p className="section-description-li">
                We make energy investing easier to understand and offer actionable steps to begin. We bring you the latest updates, expert insights, and real-world tips to help you invest with clarity and confidence.
              </p>
            </div>
          </div>
          
          <div className="row features-row-li">
            {whyLearnFeatures.map((feature, index) => (
              <div key={index} className="col-lg-3 col-md-6 mb-4">
                <div className="feature-card-li">
                  <div className="feature-icon-li">
                    <Image src={feature.icon} alt='feature.icon' />
                  </div>
                  <h4 className="feature-title-li">{feature.title}</h4>
                  <p className="feature-description-li">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Get Expert Insights in Your Inbox Section */}
      <section className="newsletter-section-li">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h2 className="newsletter-title-li">Get Expert Insights in Your Inbox</h2>
            </div>
            <div className="col-lg-6">
              <p className="newsletter-description-li">
                Get your guides, expert insights, market news, and new learning resources delivered to your inbox. Join today and learn how to invest wisely.
              </p>
              <button className="btn-subscribe-li">Subscribe Now</button>
            </div>
          </div>
        </div>
      </section>
    </div>
    {showLoginPopup && (
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 99999, overflow: 'auto' }}>
        <LoginPopUpPage onClose={() => setShowLoginPopup(false)} initialScreen="join-invest" />
      </div>
    )}
    </>
  );
}

